import React, { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { fetchWithAuth } from '../../../utils/fetchWithAuth';
import {
  parseCSV,
  transformRowToUser,
  generateResultsCSV
} from '../../../utils/csvParser';
import {
  Loader2,
  Upload,
  FileText,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Download,
  Info,
  X
} from 'lucide-react';

export const UserImportDialog = ({ open, onClose, onUsersImported }) => {
  const { t } = useTranslation();
  const [step, setStep] = useState('upload'); // 'upload' | 'preview' | 'validating' | 'importing' | 'complete'
  const [file, setFile] = useState(null);
  const [parsedData, setParsedData] = useState([]);
  const [validationErrors, setValidationErrors] = useState([]);
  const [validationWarnings, setValidationWarnings] = useState([]);
  const [validServiceDeskIds, setValidServiceDeskIds] = useState([]);
  const [importProgress, setImportProgress] = useState({ current: 0, total: 0 });
  const [importResults, setImportResults] = useState({ created: [], updated: [], failed: [] });
  const [error, setError] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef(null);

  // Reset state when dialog closes
  const handleClose = () => {
    setStep('upload');
    setFile(null);
    setParsedData([]);
    setValidationErrors([]);
    setValidationWarnings([]);
    setValidServiceDeskIds([]);
    setImportProgress({ current: 0, total: 0 });
    setImportResults({ created: [], updated: [], failed: [] });
    setError(null);
    setIsDragOver(false);
    onClose();
  };

  // Handle file selection
  const handleFileSelect = useCallback(async (selectedFile) => {
    if (!selectedFile) return;

    if (!selectedFile.name.endsWith('.csv')) {
      setError(t('The file must be in CSV format'));
      return;
    }

    setFile(selectedFile);
    setError(null);
    setStep('validating');

    try {
      const rows = await parseCSV(selectedFile);
      const users = rows.map(transformRowToUser);
      setParsedData(rows);

      // Validate on backend
      const response = await fetchWithAuth('/users/validate-import', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ users })
      });

      const json = await response.json();

      if (!response.ok) {
        throw new Error(json.error || t('Validation failed'));
      }

      if (json.success) {
        setValidationErrors(json.data.errors || []);
        setValidationWarnings(json.data.warnings || []);
        setValidServiceDeskIds(json.data.validServiceDeskIds || []);
        setStep('preview');
      } else {
        throw new Error(json.error || t('Validation failed'));
      }
    } catch (err) {
      setError(err.message);
      setStep('upload');
    }
  }, [t]);

  const handleFileInputChange = (e) => {
    const selectedFile = e.target.files?.[0];
    handleFileSelect(selectedFile);
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
    const droppedFile = e.dataTransfer.files?.[0];
    handleFileSelect(droppedFile);
  }, [handleFileSelect]);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  // Handle import
  const handleImport = async () => {
    setStep('importing');
    setError(null);

    try {
      // Transform rows to user objects
      const users = parsedData.map(transformRowToUser);
      setImportProgress({ current: 0, total: users.length });

      const response = await fetchWithAuth('/users/import', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ users })
      });

      const json = await response.json();

      if (!response.ok) {
        throw new Error(json.error || t('Import failed'));
      }

      if (json.success) {
        setImportResults(json.data);
        setImportProgress({ current: users.length, total: users.length });
        setStep('complete');
        onUsersImported();
      } else {
        throw new Error(json.error || t('Import failed'));
      }
    } catch (err) {
      console.error('Import error:', err);
      setError(err.message);
      setStep('preview');
    }
  };

  // Download results CSV
  const handleDownloadResults = () => {
    const csvContent = generateResultsCSV(importResults);
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `import-users-results-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Reset to upload step
  const handleReset = () => {
    setStep('upload');
    setFile(null);
    setParsedData([]);
    setValidationErrors([]);
    setValidationWarnings([]);
    setValidServiceDeskIds([]);
    setError(null);
  };

  const handleDownloadTemplate = () => {
    const templateContent = `Email,Nom,Role (user/technician/manager/admin/superadmin),Service Desk ID (ex: 3 ou 205 ou *),Mode (r = lecture / rw = lecture-ecriture),Secteurs (ex: L18 ou * pour tous),Service Desks Autorises - techniciens uniquement (ex: 3;205;238)
john.doe@example.com,John Doe,technician,3;205,rw,L18;L19,3;205`;

    const BOM = '\uFEFF';
    const blob = new Blob([BOM + templateContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'users-import-template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const renderUploadStep = () => (
    <>
      <div
        className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
          isDragOver
            ? 'border-primary bg-primary/5'
            : 'border-gray-300 hover:border-gray-400'
        }`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        <Upload className="w-12 h-12 mx-auto text-gray-400 mb-4" />
        <p className="text-lg font-medium text-gray-700 mb-2">
          {t('Drag and drop your CSV file here')}
        </p>
        <p className="text-sm text-gray-500 mb-4">{t('or')}</p>
        <Button
          type="button"
          variant="outline"
          onClick={() => fileInputRef.current?.click()}
        >
          {t('Browse files')}
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          className="hidden"
          onChange={handleFileInputChange}
        />
      </div>

      <div className="mt-6 p-4 bg-gray-50 rounded-lg">
        <h4 className="font-medium text-gray-700 mb-2">{t('Expected CSV format')}</h4>
        <p className="text-sm text-gray-600 mb-2">
          {t('Required columns:')} <code className="bg-gray-200 px-1 rounded">Email</code>,{' '}
          <code className="bg-gray-200 px-1 rounded">Nom</code>,{' '}
          <code className="bg-gray-200 px-1 rounded">Role</code>,{' '}
          <code className="bg-gray-200 px-1 rounded">Service Desk ID</code>
        </p>
        <p className="text-sm text-gray-600 mb-2">
          {t('Optional columns:')} <code className="bg-gray-200 px-1 rounded">Mode</code>,{' '}
          <code className="bg-gray-200 px-1 rounded">Secteurs</code>,{' '}
          <code className="bg-gray-200 px-1 rounded">{t('Authorized service desks')}</code>{' '}
          <span className="text-gray-400">{t('(technicians only)')}</span>
        </p>
        <p className="text-sm text-gray-500">
          {t('Each new user receives an email to set their password.')}
        </p>
        <p className="text-sm text-gray-500 mt-2">
          {t('Valid roles: user, technician, manager, admin, superadmin')}
        </p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="mt-3"
          onClick={handleDownloadTemplate}
        >
          <Download className="w-4 h-4 mr-2" />
          {t('Download the CSV template')}
        </Button>
      </div>

      {error && (
        <div className="mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded flex items-center gap-2">
          <XCircle className="w-5 h-5 flex-shrink-0" />
          {error}
        </div>
      )}
    </>
  );

  const renderPreviewStep = () => {
    const hasErrors = validationErrors.length > 0;
    const previewRows = parsedData.slice(0, 5);

    return (
      <>
        {file && (
          <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg mb-4">
            <FileText className="w-5 h-5 text-gray-500" />
            <span className="font-medium">{file.name}</span>
            <span className="text-sm text-gray-500">({t('{{count}} user', { count: parsedData.length })})</span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="ml-auto"
              onClick={handleReset}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        )}

        {hasErrors && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center gap-2 text-red-700 font-medium mb-2">
              <XCircle className="w-5 h-5" />
              {t('Validation errors')} ({validationErrors.length})
            </div>
            <ul className="text-sm text-red-600 space-y-1 max-h-32 overflow-y-auto">
              {validationErrors.map((err, i) => (
                <li key={i}>{err}</li>
              ))}
            </ul>
          </div>
        )}

        {validationWarnings.length > 0 && (
          <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-center gap-2 text-yellow-700 font-medium mb-2">
              <AlertTriangle className="w-5 h-5" />
              {t('Warnings')} ({validationWarnings.length})
            </div>
            <ul className="text-sm text-yellow-600 space-y-1 max-h-24 overflow-y-auto">
              {validationWarnings.map((warn, i) => (
                <li key={i}>{warn}</li>
              ))}
            </ul>
          </div>
        )}

        {!hasErrors && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center gap-2 text-green-700 font-medium">
              <CheckCircle className="w-5 h-5" />
              {t('Validation succeeded - {{count}} user ready to import', { count: parsedData.length })}
            </div>
          </div>
        )}

        <div className="mb-4">
          <h4 className="font-medium text-gray-700 mb-2">
            {t('Preview (first 5 rows)')}
          </h4>
          <div className="overflow-x-auto border rounded-lg">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">{t('Email')}</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">{t('Username')}</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">{t('Role')}</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">{t('Service Desk')}</th>
                </tr>
              </thead>
              <tbody>
                {previewRows.map((row, i) => (
                  <tr key={i} className="border-t">
                    <td className="px-3 py-2">{row.email}</td>
                    <td className="px-3 py-2">{row.username}</td>
                    <td className="px-3 py-2">{row.role}</td>
                    <td className="px-3 py-2">{row.servicedeskid}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {parsedData.length > 5 && (
            <p className="text-sm text-gray-500 mt-2">
              {t('… and {{count}} more user', { count: parsedData.length - 5 })}
            </p>
          )}
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded flex items-center gap-2">
            <XCircle className="w-5 h-5 flex-shrink-0" />
            {error}
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={handleClose}>
            {t('Cancel')}
          </Button>
          <Button
            type="button"
            onClick={handleImport}
            disabled={hasErrors}
          >
            {t('Import {{count}} user', { count: parsedData.length })}
          </Button>
        </DialogFooter>
      </>
    );
  };

  const renderValidatingStep = () => (
    <div className="py-8 text-center">
      <Loader2 className="w-12 h-12 mx-auto text-primary animate-spin mb-4" />
      <p className="text-lg font-medium text-gray-700 mb-2">
        {t('Validating in progress…')}
      </p>
      <p className="text-sm text-gray-500">
        {t('Checking the CSV file data')}
      </p>
    </div>
  );

  const renderImportingStep = () => (
    <div className="py-8 text-center">
      <Loader2 className="w-12 h-12 mx-auto text-primary animate-spin mb-4" />
      <p className="text-lg font-medium text-gray-700 mb-2">
        {t('Import in progress…')}
      </p>
      <p className="text-sm text-gray-500">
        {t('{{current}} / {{total}} users processed', { current: importProgress.current, total: importProgress.total })}
      </p>
      <div className="mt-4 w-full bg-gray-200 rounded-full h-2.5 max-w-md mx-auto">
        <div
          className="bg-primary h-2.5 rounded-full transition-all duration-300"
          style={{
            width: importProgress.total > 0
              ? `${(importProgress.current / importProgress.total) * 100}%`
              : '0%'
          }}
        />
      </div>
    </div>
  );

  const renderCompleteStep = () => {
    const { created, updated, failed } = importResults;

    return (
      <>
        <div className="py-4 text-center">
          <CheckCircle className="w-12 h-12 mx-auto text-green-500 mb-4" />
          <p className="text-lg font-medium text-gray-700 mb-4">
            {t('Import complete')}
          </p>
        </div>

        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-center">
            <p className="text-2xl font-bold text-green-700">{created.length}</p>
            <p className="text-sm text-green-600">{t('Created users')}</p>
          </div>
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg text-center">
            <p className="text-2xl font-bold text-blue-700">{updated.length}</p>
            <p className="text-sm text-blue-600">{t('Updated')}</p>
          </div>
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-center">
            <p className="text-2xl font-bold text-red-700">{failed.length}</p>
            <p className="text-sm text-red-600">{t('Failures')}</p>
          </div>
        </div>

        {failed.length > 0 && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="font-medium text-red-700 mb-2">{t('Errors')}</p>
            <ul className="text-sm text-red-600 space-y-1 max-h-24 overflow-y-auto">
              {failed.map((item, i) => (
                <li key={i}>{item.email}: {item.error}</li>
              ))}
            </ul>
          </div>
        )}

        {created.length > 0 && (
          <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-2 text-sm text-blue-700">
            <Info className="w-5 h-5 flex-shrink-0" />
            {t('Created users receive an email to set their password.')}
          </div>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleDownloadResults}
          >
            <Download className="w-4 h-4 mr-2" />
            {t('Download the report')}
          </Button>
          <Button type="button" onClick={handleClose}>
            {t('Close')}
          </Button>
        </DialogFooter>
      </>
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>{t('Import users')}</DialogTitle>
          <DialogDescription>
            {step === 'upload' && t('Import a list of users from a CSV file')}
            {step === 'validating' && t('Validating in progress…')}
            {step === 'preview' && t('Check the data before import')}
            {step === 'importing' && t('Import in progress…')}
            {step === 'complete' && t('Import complete')}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto">
          {step === 'upload' && renderUploadStep()}
          {step === 'validating' && renderValidatingStep()}
          {step === 'preview' && renderPreviewStep()}
          {step === 'importing' && renderImportingStep()}
          {step === 'complete' && renderCompleteStep()}
        </div>

        {step === 'upload' && (
          <DialogFooter className="flex-shrink-0">
            <Button type="button" variant="outline" onClick={handleClose}>
              {t('Cancel')}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};
